import { useState, useCallback } from "react";
import { enqueueSnackbar } from "notistack";

import {
  getAppointments,
  replyAppointment,
  closeAppointment,
} from "../../../services/AppManagement/AppointmentService";
import { getMeetingWithTypes } from "../../../services/AppManagement/MeetingWithTypeService";

export const useAppointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [paginationMeta, setPaginationMeta] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: 10,
  });
  const [meetingAuthorityOptions, setMeetingAuthorityOptions] = useState([]);
  const [isLoader, setIsLoader] = useState(false);

  /**
   * Fetch appointments
   */
  const fetchAppointments = useCallback(async (params = {}) => {
    try {
      setIsLoader(true);

      const response = await getAppointments(params);

      if (response.data.success) {
        const { data, current_page, last_page, total, per_page } = response.data;
        setAppointments(data || []);
        setPaginationMeta({ current_page, last_page, total, per_page });
      }
    } catch (error) {
      enqueueSnackbar("Failed to fetch appointments", {
        variant: "error",
      });
    } finally {
      setIsLoader(false);
    }
  }, []);

  /**
   * Reply appointment
   */
  const replyToAppointment = useCallback(async (id, data) => {
    try {
      const response = await replyAppointment(id, data);
      if (response.data.success) {
        enqueueSnackbar(response.data.message.success, {
          variant: "success",
        });

        await fetchAppointments();

        return true;
      }
    } catch (error) {
      enqueueSnackbar("Failed to reply appointment", {
        variant: "error",
      });
    }

    return false;
  }, [fetchAppointments]);

  /**
   * Close appointment
   */
  const closeAppointmentById = useCallback(async (id) => {
    try {
      const response = await closeAppointment(id);

      if (response.data.success) {
        enqueueSnackbar(response.data.message.success, {
          variant: "success",
        });

        await fetchAppointments();

        return true;
      }
    } catch (error) {
      enqueueSnackbar("Failed to close appointment", {
        variant: "error",
      });
    }

    return false;
  }, [fetchAppointments]);

  /**
   * Fetch meeting authority options from Meeting With Types
   */
  const fetchMeetingAuthorities = useCallback(async () => {
    try {
      const response = await getMeetingWithTypes();
      const rows = response?.data?.data || response?.data || [];

      const options = rows
        .map((item) => ({
          id: item?.name,
          name: item?.name,
        }))
        .filter((item) => item.id && item.name);

      const uniqueOptions = Array.from(
        new Map(options.map((opt) => [opt.id, opt])).values(),
      );

      setMeetingAuthorityOptions(uniqueOptions);
    } catch (error) {
      enqueueSnackbar("Failed to fetch meeting authorities", {
        variant: "error",
      });
    }
  }, []);

  return {
    appointments,
    paginationMeta,
    meetingAuthorityOptions,
    isLoader,
    fetchAppointments,
    replyToAppointment,
    closeAppointmentById,
    fetchMeetingAuthorities,
  };
};